// Pure pagination math for a practitioner NOTE card in the bloodwork-analysis
// PDF — today the consolidated Section Notes card, which is the one that runs
// long enough to need it.
//
// WHY this exists rather than letting html2pdf flow the card: html2pdf skips
// its keep-together pass for anything taller than a page and then simply slices
// the captured canvas on a fixed grid. Nothing in that slice knows where the
// text is, so a card over one page tall printed its last few lines underneath
// the jsPDF footer (which is drawn over the bottom of the content area) and cut
// a section heading in half across the page boundary. Every other long block in
// this report is pre-paginated for the same reason — see pdfMooPagination,
// pdfSupplementsPagination and pdfHolobiomePagination.
//
// The unit here is a stored LINE of the note, measured with the same arithmetic
// the card's height estimate uses (noteLineHeights). Two rules shape the split
// beyond "fill until full":
//
//   - a `## Label` heading is never left at the bottom of a page with nothing
//     under it; it moves down to the page its notes are on;
//   - when a section's notes DO spill, the next page reopens them under
//     `## Label (cont.)`, so a reader meeting a page mid-section still knows
//     whose notes they are reading. Same convention the MOO section uses.
import { noteLineHeights, NOTE_HEADING_HEIGHT } from '../helpers/pdfPanelMetrics';

export type NoteCardPaginationInput = {
  /** The card's body text, `## Label` headings and all. */
  text: string;
  /**
   * Usable height of one page, in capture px. Deliberately well under the true
   * page height — that slack absorbs both the estimate's error and the footer
   * printed over the bottom of the page.
   */
  pageCapacity: number;
  /** The card's own chrome (header + body padding), charged once per page. */
  cardOverhead: number;
  /** Height of one wrapped row of body text. */
  lineHeight: number;
};

const isHeading = (line: string): boolean => line.trim().startsWith('## ');
const headingLabel = (line: string): string => line.trim().slice(3).trim();
const isBlank = (line: string): boolean => line.trim() === '';

/** Drop blank lines from both ends of a page — renderNoteBody trims them anyway. */
function trimBlankEdges(lines: string[]): string[] {
  let start = 0;
  let end = lines.length;
  while (start < end && isBlank(lines[start])) start++;
  while (end > start && isBlank(lines[end - 1])) end--;
  return lines.slice(start, end);
}

/**
 * Pull a heading off the end of a full page so it travels to the page its notes
 * landed on. Returns the lines to carry (empty when the page doesn't end on a
 * heading, or when carrying it would leave the page with nothing on it).
 */
function carryTrailingHeading(page: string[]): string[] {
  let i = page.length - 1;
  while (i >= 0 && isBlank(page[i])) i--;
  if (i < 0 || !isHeading(page[i])) return [];
  // Anything left above it? A page holding only this heading keeps it: moving
  // it would flush an empty page, and the next page opens with it regardless.
  const hasContentAbove = page.slice(0, i).some(l => !isBlank(l));
  if (!hasContentAbove) return [];
  const carried = page.slice(i, i + 1);
  page.length = i;
  return carried;
}

/**
 * Slice a note card's text into one string per PDF page. A card that fits gets
 * a single-element array, so the caller can always map over the result.
 */
export function paginateNoteCardText(input: NoteCardPaginationInput): string[] {
  const { text, pageCapacity, cardOverhead, lineHeight } = input;
  if (!(text || '').trim()) return [];

  const lines = text.split('\n');
  // Measured once, up front: a blank line's cost depends on its neighbours, so
  // a line can't be measured in isolation. Index-aligned with `lines`.
  const heights = noteLineHeights(lines, lineHeight);
  const pages: string[][] = [];
  let page: string[] = [];
  let used = cardOverhead;
  // The heading whose notes we are currently inside, so a spill can reopen it.
  let openHeading: string | null = null;
  // Set when a page break lands mid-section: the next page reopens the section
  // under `## Label (cont.)`. Held rather than written at the break because the
  // break can fall on the blank line between two sections, and the next section
  // opens with a heading of its own — writing it eagerly there would print a
  // continuation heading with nothing under it.
  let reopenHeading: string | null = null;

  const push = (line: string, height: number) => {
    page.push(line);
    used += height;
  };

  lines.forEach((line, i) => {
    const height = heights[i];
    // `page.length` guards the break: a single line taller than a whole page
    // still lands somewhere instead of flushing empty pages forever.
    if (page.length > 0 && used + height > pageCapacity) {
      const carried = carryTrailingHeading(page);
      pages.push(trimBlankEdges(page));
      page = [];
      used = cardOverhead;
      // A heading that moved down opens its own section again; only a genuine
      // mid-section spill needs the "(cont.)" line.
      reopenHeading = carried.length > 0 ? null : openHeading;
      carried.forEach(h => push(h, NOTE_HEADING_HEIGHT));
    }
    if (isHeading(line)) {
      openHeading = headingLabel(line);
      reopenHeading = null;
    } else if (isBlank(line) && page.length === 0) {
      // Spacing at a page edge no longer separates anything, and it would push
      // the first real line down the fresh page.
      return;
    } else if (reopenHeading && page.length === 0) {
      push(`## ${reopenHeading} (cont.)`, NOTE_HEADING_HEIGHT);
      reopenHeading = null;
    }
    push(line, height);
  });

  const last = trimBlankEdges(page);
  if (last.length > 0) pages.push(last);

  return pages.filter(p => p.length > 0).map(p => p.join('\n'));
}
